import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { PasswordInput } from "@admin/components/atoms/Input/PasswordInput";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  addCompany,
  passwordValidation,
  repasswordValidation,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

type AddNewCompanyModalProps = ModalProps;

export function AddNewCompanyModal({ ...props }: AddNewCompanyModalProps) {
  const { t } = useTranslation();

  const createNewCompanySchema = z
    .object({
      companyCode: z.string().length(4, "Must be four digit"),
      companyName: z.string().min(1, "Company name required"),
      adminFirstName: z.string().min(1, "Admin name required"),
      adminSurname: z.string().min(1, "Admin surname required"),
      adminUsername: z.string().min(1, "Admin username required"),
      adminPhone: z.string().min(1, "Admin phone required"),
      adminEmail: z.string().email("Invalid email format"),
      adminPassword: passwordValidation(t),
      adminPasswordAgain: repasswordValidation(t),
    })
    .refine((data) => data.adminPassword === data.adminPasswordAgain, {
      message: "Passwords must match",
      path: ["adminPasswordAgain"],
    });

  type AddNewCompanyData = z.infer<typeof createNewCompanySchema>;

  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    reset,
    formState: state,
  } = useForm<AddNewCompanyData>({
    resolver: zodResolver(createNewCompanySchema),
  });

  const createCompanyMutation = useMutation({
    mutationFn: addCompany,
    onSuccess: () => {
      reset();
      props?.onClose?.();
      toast.success(t("settings.New company has been created"), {
        duration: 4000,
      });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
    onError: (err) => {
      console.error("Mutation error:", err.message);
      toast.error(t("settings.Unable to create new company"), {
        duration: 4000,
      });
    },
  });
  return (
    <Modal
      {...props}
      title="Add New Company"
      actionButtons={
        <>
          <Button
            disabled={createCompanyMutation.isPending}
            variant="secondary"
            onClick={props?.onClose}
          >
            {t("common.Discard")}
          </Button>
          <Button
            onClick={handleSubmit(async (data) => {
              const payload = {
                companyName: data.companyName,
                firstName: data.adminFirstName,
                lastName: data.adminSurname,
                phone: data.adminPhone,
                email: data.adminEmail,
                password: data.adminPassword,
                userName: data.adminUsername,
                companyCode: data.companyCode,
              };
              createCompanyMutation.mutate(payload);
            })}
            loading={createCompanyMutation.isPending}
            disabled={createCompanyMutation.isPending}
          >
            {t("common.Save Changes")}
          </Button>
        </>
      }
    >
      <form>
        <label className="text-gray-900 text-[15px] font-[600]">
          Company Information
        </label>
        <div className="flex flex-col">
          <Label title="Company Name" required>
            <Input {...register("companyName")} name="companyName" size="sm" />
          </Label>
          <ErrorMessage message={state.errors.companyName?.message} />
        </div>
        <div className="flex flex-col">
          <Label title="Company Code" required>
            <Input
              {...register("companyCode")}
              name="companyCode"
              size="sm"
              type="number"
            />
          </Label>
          <ErrorMessage message={state.errors.companyCode?.message} />
        </div>
        <div className="flex flex-col">
          <Label title="Country" required>
            <Input name="code" size="sm" disabled value={"🇩🇰 Denmark"} />
          </Label>
        </div>
        <div className="border-b border-b-gray-200 my-2.5"></div>
        <label className="text-gray-900 text-[15px] font-[600]">
          Admin Information
        </label>
        <div className="flex flex-col">
          <Label title="Admin Name" required>
            <Input
              {...register("adminFirstName")}
              name="adminFirstName"
              size="sm"
            />
          </Label>
          <ErrorMessage message={state.errors.adminFirstName?.message} />
        </div>
        <div className="flex flex-col">
          <Label title="Admin Surname" required>
            <Input
              {...register("adminSurname")}
              name="adminSurname"
              size="sm"
            />
          </Label>
          <ErrorMessage message={state.errors.adminSurname?.message} />
        </div>
        <div className="flex flex-col">
          <Label title="Admin Username" required>
            <Input
              {...register("adminUsername")}
              name="adminUsername"
              size="sm"
            />
          </Label>
          <ErrorMessage message={state.errors.adminUsername?.message} />
        </div>
        <div className="flex flex-col">
          <Label title="Admin Email" required>
            <Input {...register("adminEmail")} name="adminEmail" size="sm" />
          </Label>
          <ErrorMessage message={state.errors.adminEmail?.message} />
        </div>
        <div className="flex flex-col">
          <Label title="Admin Phone" required>
            <Input {...register("adminPhone")} name="adminPhone" size="sm" />
          </Label>
          <ErrorMessage message={state.errors.adminPhone?.message} />
        </div>

        <div className="border-b border-b-gray-200 my-2.5"></div>
        <label className="text-gray-900 text-[15px] font-[600]">
          Admin Password
        </label>
        <div className="flex flex-col">
          <Label title="Admin Password" required>
            <PasswordInput
              {...register("adminPassword")}
              placeholder={t("common.Enter Password")}
              name="adminPassword"
            />
          </Label>
          <ErrorMessage message={state.errors.adminPassword?.message} />
        </div>
        <div className="flex flex-col">
          <Label title="Admin Password Again" required>
            <PasswordInput
              {...register("adminPasswordAgain")}
              placeholder={t("common.Enter Password Again")}
              name="adminPasswordAgain"
            />
          </Label>
          <ErrorMessage message={state.errors.adminPasswordAgain?.message} />
        </div>
      </form>
    </Modal>
  );
}
